import { Body, Heading } from 'copilot-design-system';
import { useMemo } from 'react';

import { notify } from 'src/clients/ApiService';
import { validateStringType } from 'src/features/assistant/action-item/ActionItem';
import { AssistantError } from 'src/features/assistant/action-item/AssistantError';
import {
  WidgetContainer,
  WidgetContent,
  WidgetDraft,
  WidgetHeader,
  WidgetItem,
} from 'src/features/assistant/action-item/Widget';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  ApiTags,
  appAPI,
  generateErrorMessageForShareContract,
  ShareContractTemplateInput,
  useGetContractTemplatesQuery,
  useShareContractTemplateMutation,
} from 'src/services/api';
import { RootState } from 'src/store';
import { CTA } from 'src/types/stream-chat';
import { DateUtils } from 'src/utils';
import { getProfileFromUser, UserProfile } from 'src/utils/UserUtils';

export const ShareContractDraft = ({
  cta,
  onCTAClick,
}: {
  cta: CTA;
  onCTAClick: (cta: CTA) => void;
}) => {
  const contractDetails = cta.contract_data;
  const {
    data: contractTemplates = [],
    error: contractTemplateError,
    refetch: contractRefetch,
  } = useGetContractTemplatesQuery();
  const clients = useAppSelector((state: RootState) => state.clients.clients);
  const matchingContractTemplates = useMemo(() => {
    return contractTemplates.find(
      (template) => template.id === contractDetails?.contractTemplateId,
    );
  }, [contractTemplates, contractDetails]);
  const dispatch = useAppDispatch();
  const [shareContractTemplate] = useShareContractTemplateMutation();

  const handleShareContract = async () => {
    const { clientId, companyId, contractTemplateId } = cta.contract_data || {};

    const recipientIds = validateStringType(clientId)
      ? clientId
      : validateStringType(companyId)
        ? companyId
        : '';

    const requestBody: ShareContractTemplateInput = {
      clientId: validateStringType(clientId) ? clientId : '',
      companyId: validateStringType(companyId) ? companyId : '',
      contractTemplateId: validateStringType(contractTemplateId)
        ? contractTemplateId
        : '',
      creationMode: 'template',
    };

    const queries = [
      shareContractTemplate(requestBody)
        .unwrap()
        .catch((e) => ({
          error: e,
          recipientIds,
        })),
    ];

    const results = await Promise.all(queries);

    const erroredRequests = results.filter((r) => 'error' in r);

    if (erroredRequests.length > 0) {
      const failedRequestClients: UserProfile[] = erroredRequests
        .map((request) => {
          const client = clients.find(
            (c) =>
              'recipient' in request && c.id === cta.contract_data?.clientId,
          );
          if (!client) return null;
          return getProfileFromUser(client);
        })
        .filter((c): c is UserProfile => c !== null);

      notify({
        status: 'error',
        errorMessage:
          generateErrorMessageForShareContract(failedRequestClients),
        dispatch,
      });
      return;
    }

    dispatch(
      appAPI.util.invalidateTags([
        ApiTags.contract_templates,
        ApiTags.contracts,
      ]),
    );
    notify({
      status: 'success',
      successMessage: 'Contract template shared successfully.',
      dispatch,
    });
  };

  const handleCTAClick = async (actionCTA: CTA) => {
    if (contractTemplateError) {
      contractRefetch();
      return;
    } else {
      await handleShareContract();

      onCTAClick(actionCTA);
    }
  };

  return (
    <WidgetContainer>
      <WidgetHeader
        widgetName="SHARE CONTRACT"
        buttonName={
          !matchingContractTemplates || contractTemplateError
            ? 'Retry'
            : 'Share contract'
        }
        buttonIcon={
          !matchingContractTemplates || contractTemplateError
            ? 'Repeat'
            : 'Send'
        }
        onClick={() => handleCTAClick(cta)}
      />
      {!matchingContractTemplates || contractTemplateError ? (
        <AssistantError errorMessage="Unable to load contract" />
      ) : (
        <WidgetContent>
          <WidgetDraft>
            <Heading>{matchingContractTemplates?.name || 'Contract'}</Heading>
          </WidgetDraft>

          <WidgetItem className="border-l-2 border-r-0 border-t-0 border-b-0 border-solid border-non-hover-border pl-4 grid grid-cols-2 auto-rows-[28px] items-center mt-2">
            <Body size="sm" className="text-gray-small">
              Total submissions
            </Body>
            <Body size="sm">{matchingContractTemplates?.submissionsCount}</Body>
            <Body size="sm" className="text-gray-small">
              Last submission
            </Body>
            <Body size="sm">
              {DateUtils.formatDate(
                matchingContractTemplates?.latestSubmissionDate,
              )}
            </Body>
          </WidgetItem>
        </WidgetContent>
      )}
    </WidgetContainer>
  );
};
